import {
  Button,
  Code,
  Group,
  MultiSelect,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGetApplicationList } from '@/queries/applicationQueries'
import { WorkspaceApiKeyScope } from '@/types/workspace'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { SCOPE_OPTIONS } from './apiKeyUtils'

type FormValues = {
  name: string
  scope: WorkspaceApiKeyScope
  applicationIds: string[]
}

export type SubmitValues = FormValues

type CreateApiKeyFormProps = {
  onCancel: () => void
  onSubmit: (values: SubmitValues) => void
}

export const CreateApiKeyForm = ({
  onCancel,
  onSubmit
}: CreateApiKeyFormProps) => {
  const intl = useIntl()

  const { data, isLoading } = useGetApplicationList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'name'
  })

  const applications = data?.results || []

  const applicationOptions = applications.map((app) => ({
    value: app.id,
    label: app.name
  }))

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      scope: WorkspaceApiKeyScope.PlatformEvents,
      applicationIds: []
    },
    validate: {
      name: isNotEmpty(
        intl.formatMessage({ id: 'workspace.apiKeys.form.name.required' })
      ),
      applicationIds: isNotEmpty(
        intl.formatMessage({
          id: 'workspace.apiKeys.form.applications.required'
        })
      )
    }
  })

  const handleSubmit = (values: FormValues) => {
    onSubmit(values)
  }

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="lg">
        <TextInput
          label={<FormattedMessage id="workspace.apiKeys.form.name" />}
          placeholder={intl.formatMessage({
            id: 'workspace.apiKeys.form.name.placeholder'
          })}
          description={
            <FormattedMessage id="workspace.apiKeys.form.name.description" />
          }
          inputWrapperOrder={['label', 'description', 'input', 'error']}
          autoFocus
          data-autofocus // required for auto focus inside the modal
          {...form.getInputProps('name')}
        />

        <Radio.Group
          label={<FormattedMessage id="workspace.apiKeys.form.scope" />}
          description={
            <FormattedMessage id="workspace.apiKeys.form.scope.description" />
          }
          inputWrapperOrder={['label', 'description', 'input', 'error']}
          {...form.getInputProps('scope')}
        >
          <Paper p="md" mt={4} withBorder>
            <Stack gap="lg">
              {SCOPE_OPTIONS.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  label={
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>
                        <FormattedMessage id={option.labelId} />
                      </Text>
                      <Text size="xs" c="dimmed">
                        <FormattedMessage id={option.descriptionId} />
                      </Text>
                      <Code fz="xs">{option.endpoint}</Code>
                    </Stack>
                  }
                />
              ))}
            </Stack>
          </Paper>
        </Radio.Group>

        <MultiSelect
          label={<FormattedMessage id="workspace.apiKeys.form.applications" />}
          placeholder={intl.formatMessage({
            id: 'workspace.apiKeys.form.applications.placeholder'
          })}
          description={
            <FormattedMessage id="workspace.apiKeys.form.applications.description" />
          }
          inputWrapperOrder={['label', 'description', 'input', 'error']}
          data={applicationOptions}
          disabled={isLoading}
          searchable
          clearable
          {...form.getInputProps('applicationIds')}
        />
      </Stack>

      <Group justify="end" mt={60}>
        <Button miw={120} variant="default" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button miw={120} type="submit">
          <FormattedMessage id="workspace.apiKeys.create" />
        </Button>
      </Group>
    </form>
  )
}
